import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Typography,
} from '@material-ui/core';
import * as Yup from 'yup';
import FormikForm, { FormikStep } from 'src/components/ui-components/FormikForm';
import InputField from 'src/components/ui-components/InputField';
import SwitchField from 'src/components/ui-components/SwitchField';
import AlertMessage from 'src/components/ui-components/AlertMessage';
import { InsertUpdatePageFilter } from './ConnectionService';
import { FieldArray } from 'formik';
import { AddCircle } from '@material-ui/icons';
import DeleteIcon from '@material-ui/icons/Delete';
import CustomSkeleton from 'src/components/skeleton/Skeleton';

const defaultForm = {
  pageFilterName: '',
  pageFilterType: '',
  pageFilterFieldsSeparater: '',
  hasConfigurableWidgets: false,
  isEnabled: false,
  pageFilterValues: [],
};

const FilterCreateEdit = ({ filter, onClickCancel, onSaveFilter }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showFilterValues, setShowFilterValues] = useState<boolean>(false);
  const formRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: {
          ...data, isEdit: !!filter
        },
      });
    }
  };

  const handleToggleIsDefault = (idx: number) => {
    const pageFilterValues = [...formRef.current.values.pageFilterValues];
    const defaultCount = pageFilterValues.filter(value => value.isDefault).length;

    if (defaultCount === 1 && pageFilterValues[idx].isDefault) {
      AlertMessage({ type: 'warning', msg: 'Filter must have a default value' });
      return;
    }

    const updatedPageFilterValues = pageFilterValues.map((value, index) => {
      if (index === idx) {
        value.isDefault = !value.isDefault;
        if (!value.isEnabled) {
          value.isEnabled = true;
        }
      } else {
        value.isDefault = false;
      }
      return value;
    });

    if (updatedPageFilterValues.filter(value => value.isDefault).length === 0) {
      AlertMessage({ type: 'warning', msg: 'Filter must have a default value' });
      formRef.current.setFieldValue('pageFilterValues', pageFilterValues);
    } else {
      formRef.current.setFieldValue('pageFilterValues', updatedPageFilterValues);
    }
  };

  useEffect(() => {
    if (filter) {
      resetFormData({
        pageFilterName: filter.pageFilterName || '',
        pageFilterType: filter.pageFilterType || '',
        pageFilterFieldsSeparater: filter.pageFilterFieldsSeparater || '',
        hasConfigurableWidgets: filter.hasConfigurableWidgets || false,
        isEnabled: filter.isEnabled || false,
        pageFilterValues: filter.pageFilterValues || [],
      });
      setShowFilterValues(!!(filter.pageFilterValues && filter.pageFilterValues.length > 0));
    } else {
      resetFormData(defaultForm);
      setShowFilterValues(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleSave = async (values) => {
    setIsLoading(true);

    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => {
        if (typeof value === 'string') {
          return [key, value.trim()];
        } else if (Array.isArray(value) && key === 'pageFilterValues') {
          return [key, value.map(item => {
            const trimmedItem = {};
            for (const [itemKey, itemValue] of Object.entries(item)) {
              trimmedItem[itemKey] = typeof itemValue === 'string' ? itemValue.trim() : itemValue;
            }
            return trimmedItem;
          })];
        } else {
          return [key, value];
        }
      })
    );

    const requestObject = {
      ...trimmedValues,
      pageFilterKey: filter ? filter.pageFilterKey : null,
    };

    try {
      await InsertUpdatePageFilter(requestObject);
      const msg = filter ? "Filter updated successfully" : "Filter created successfully";
      AlertMessage({ type: 'success', msg });
      onSaveFilter(requestObject);
    } catch (err) {
      Object.entries(err.errors).forEach(([, errMsg]: any) => {
        AlertMessage({ type: 'error', msg: errMsg.errorMessage });
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickOpen = (index) => {
    setDeleteIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteIndex(null);
  };

  const handleConfirmDelete = () => {
    formRef.current.setFieldValue('pageFilterValues', formRef.current.values.pageFilterValues.filter((_, i) => i !== deleteIndex));
    setOpen(false);
    AlertMessage({ type: 'success', msg: 'Filter Values deleted successfully' });
    if (formRef.current?.values?.pageFilterValues?.length === 1) {
      setShowFilterValues(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">Filter Details</Typography>
        <Grid style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            sx={{ background: 'linear-gradient(to bottom, #939ca3, #939ca3)', minWidth: 100 }}
            variant="contained"
            onClick={onClickCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              background:
                "linear-gradient(to bottom, #072f40, #072f40)",
            }}
            style={{ marginLeft: '10px' }}
            onClick={() => {
              formRef.current.submitForm()
            }}
          >
            Save
            {isLoading && (
              <CircularProgress
                color="inherit"
                thickness={5}
                size={20}
                sx={{ ml: 1 }}
              />
            )}
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {isLoading && <CustomSkeleton skeletonType="FormEdit" />}
        <Box sx={{ display: isLoading ? 'none' : '' }}>
          <FormikForm
            innerRef={formRef}
            initialValues={defaultForm}
            onSubmit={handleSave}
          >
            <FormikStep
              validationSchema={Yup.object({
                pageFilterName: Yup.string()
                  .required('Page Filter Name is required')
                  .max(100, 'Page Filter Name cannot be more than 100 characters')
                  .matches(/^[a-zA-Z0-9\s]+$/, 'Page Filter Name should not contain special characters')
                  .nullable(),
                pageFilterType: Yup.string()
                  .required('Page Filter Type is required')
                  .max(50, 'Page Filter Type cannot be more than 50 characters')
                  .matches(/^[a-zA-Z0-9\s]+$/, 'Page Filter Type should not contain special characters')
                  .nullable(),
                pageFilterFieldsSeparater: Yup.string()
                  .max(10, 'Page Filter Fields Separater cannot be more than 10 characters')
                  .matches(/^[a-zA-Z0-9\s]+$/, 'Page Filter Fields Separater should not contain special characters')
                  .nullable(),
                isEnabled: Yup.boolean().nullable(),
                hasConfigurableWidgets: Yup.boolean().nullable(),
                pageFilterValues: Yup.array()
                  .when(['$pageFilterValues'], {
                    is: (pageFilterValues) => pageFilterValues && pageFilterValues.length > 0,
                    then: Yup.array().of(
                      Yup.object().shape({
                        pageFilterValueName: Yup.string()
                          .max(250, 'Value Name cannot be more than 250 characters')
                          .matches(/^[a-zA-Z0-9\s]+$/, 'Value Name should not contain special characters')
                          .required('Value Name is required'),
                        pageFilterValue: Yup.string()
                          .max(500, 'Value cannot be more than 500 characters')
                          .matches(/^[a-zA-Z0-9\s]+$/, 'Value should not contain special characters')
                          .required('Value is required'),
                        sortOrder: Yup.string()
                          .required('Sort Order is required')
                          .matches(/^\S*$/, 'Sort order cannot contain spaces')
                          .matches(/^[1-9][0-9]*$/, 'Please enter a valid number')
                          .test('is-valid-sort-order', function (value) {
                            const { pageFilterValues } = this.options.context;
                            return value <= pageFilterValues.length ? true : this.createError({ message: `Sort Order cannot exceed ${pageFilterValues.length}` });
                          })
                          .test({
                            name: 'is-unique-sort-order',
                            message: 'Sort Order must be unique',
                            test: function (value) {
                              const { pageFilterValues } = this.options.context;
                              const sortOrderValues = pageFilterValues.map(item => Number(item.sortOrder));
                              return sortOrderValues.indexOf(Number(value)) === sortOrderValues.lastIndexOf(Number(value));
                            }
                          })
                          .nullable(),
                        isDefault: Yup.boolean().nullable(),
                      })
                    ),
                    otherwise: Yup.array().nullable(),
                  }),
              })}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InputField label="Page Filter Name *" name="pageFilterName" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField label="Page Filter Type *" name="pageFilterType" disabled onChange={() => {}} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField label="Page Filter Fields Separator" name="pageFilterFieldsSeparater" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SwitchField label="Is Enabled" name="isEnabled" labelPlacement='start' />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: '10px' }}>Add Page Filter Values</Typography>
                  {!showFilterValues ? (
                    <Button
                      sx={{ background: "linear-gradient(to bottom, #072f40, #072f40)", minWidth: 100, mt: 2 }}
                      type="button"
                      variant="contained"
                      startIcon={<AddCircle />}
                      onClick={() => {
                        setShowFilterValues(true);
                        formRef.current.setFieldValue('pageFilterValues', [{ pageFilterValueName: '', pageFilterValue: '', sortOrder: 1, isDefault: true }]);
                      }}
                    >
                      Add
                    </Button>
                  ) : (
                    <FieldArray name="pageFilterValues">
                      {({ push }) => (
                        <>
                          {formRef.current?.values?.pageFilterValues?.map((value, index) => (
                            <Grid container spacing={2} key={index} sx={{ mt: '1px' }}>
                              <Grid item xs={12} sm={3}>
                                <InputField label="Value Name *" name={`pageFilterValues.${index}.pageFilterValueName`} />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <InputField label="Value *" name={`pageFilterValues.${index}.pageFilterValue`} />
                              </Grid>
                              <Grid item xs={12} sm={2}>
                                <InputField label="Sort Order *" name={`pageFilterValues.${index}.sortOrder`} />
                              </Grid>
                              <Grid item xs={12} sm={1}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={formRef.current.values.pageFilterValues[index].isDefault === true}
                                      onChange={() => handleToggleIsDefault(index)}
                                    />
                                  }
                                  label="Default"
                                  labelPlacement="top"
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <IconButton
                                  sx={{ p: '10px', ml: 2, mt: 1 }}
                                  onClick={() => handleClickOpen(index)}
                                >
                                  <DeleteIcon fontSize="medium" />
                                </IconButton>
                                {index === formRef.current?.values?.pageFilterValues.length - 1 && (
                                  <Button
                                    sx={{ background: "linear-gradient(to bottom, #072f40, #072f40)", minWidth: 100, ml: 2, mt: 1 }}
                                    type="button"
                                    variant="contained"
                                    startIcon={<AddCircle />}
                                    onClick={() => push({ pageFilterValueName: '', pageFilterValue: '', sortOrder: formRef.current.values.pageFilterValues.length + 1, isDefault: false })}
                                    disabled={!(value.pageFilterValueName && value.pageFilterValue && value.sortOrder)}
                                  >
                                    Add
                                  </Button>
                                )}
                              </Grid>
                            </Grid>
                          ))}
                        </>
                      )}
                    </FieldArray>
                  )}
                </Grid>
              </Grid>
            </FormikStep>
          </FormikForm>
        </Box>
      </Grid>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this filter value?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant='outlined' color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant='contained' color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default FilterCreateEdit;
